import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import { ProtocolosURL, rutasxEmpresaURL } from "../../API/apiurls";
import { useGlobalState } from "../../Context/GlobalStateContext";

export function DispositivosModal({
  mostrar,
  cerrar,
  guardar,
  editar,
  datosaeditar,
  title,
}) {
  const { userData } = useGlobalState();
  const empresaId = Number(userData?.empresaId || localStorage.getItem("empresaid") || 0);

  const isEdit = Boolean(datosaeditar?.id);

  const [loadingRutas, setLoadingRutas] = useState(false);
  const [loadingProtocolos, setLoadingProtocolos] = useState(false);
  const [rutas, setRutas] = useState([]);
  const [protocolos, setProtocolos] = useState([]);

  const [formData, setFormData] = useState({
    nombre: "",
    ruta: "",
    velocidad: "",
    volumen: "",
    protocoloId: "",
    metodoComunicacion: "HTTP",
    estado: true,
  });

  const protocoloDefaultId = useMemo(() => {
    const mt2 = protocolos.find((protocolo) => String(protocolo?.codigo || "").toUpperCase() === "MT-2");
    return String(mt2?.id || protocolos[0]?.id || "");
  }, [protocolos]);

  const protocoloSeleccionado = useMemo(
    () => protocolos.find((protocolo) => String(protocolo?.id || "") === String(formData.protocoloId || "")),
    [protocolos, formData.protocoloId]
  );

  const mqttPermitido = useMemo(() => {
    const codigo = String(protocoloSeleccionado?.codigo || "").toUpperCase().replace(/\s+/g, "").replace(/_/g, "-");
    return codigo === "MT-3" || codigo === "MT-03" || codigo === "MT-3-JSON" || codigo === "MT-03-JSON";
  }, [protocoloSeleccionado]);

  const cargarRutas = async () => {
    if (!empresaId) return;

    try {
      setLoadingRutas(true);
      const response = await axios.get(rutasxEmpresaURL, {
        params: { empresaId, estado: true },
      });

      const list = response.data || [];
      setRutas(list);
    } catch (error) {
      console.error("Error al listar rutas", error);
      setRutas([]);
    } finally {
      setLoadingRutas(false);
    }
  };

  const cargarProtocolos = async () => {
    try {
      setLoadingProtocolos(true);
      const response = await axios.get(ProtocolosURL);
      setProtocolos(response.data || []);
    } catch (error) {
      console.error("Error al listar protocolos", error);
      setProtocolos([]);
    } finally {
      setLoadingProtocolos(false);
    }
  };

  useEffect(() => {
    if (!mostrar) return;
    cargarRutas();
    cargarProtocolos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrar, empresaId]);

  useEffect(() => {
    if (!mostrar) return;

    if (isEdit) {
      setFormData({
        nombre: datosaeditar?.nombre ?? "",
        ruta: datosaeditar?.rutasModel?.id ? String(datosaeditar.rutasModel.id) : "",
        velocidad: datosaeditar?.velocidad ?? "",
        volumen: datosaeditar?.volumen ?? "",
        protocoloId: datosaeditar?.protocolosModel?.id ? String(datosaeditar.protocolosModel.id) : protocoloDefaultId,
        metodoComunicacion: String(datosaeditar?.metodoComunicacion ?? datosaeditar?.metodo_comunicacion ?? "HTTP").toUpperCase(),
        estado: datosaeditar?.estado ?? true,
      });
    } else {
      setFormData({
        nombre: "",
        ruta: "",
        velocidad: "",
        volumen: "",
        protocoloId: protocoloDefaultId,
        metodoComunicacion: "HTTP",
        estado: true,
      });
    }
  }, [mostrar, isEdit, datosaeditar, protocoloDefaultId]);

  useEffect(() => {
    if (!mostrar) return;
    if (!mqttPermitido && formData.metodoComunicacion === "MQTT") {
      setFormData((prev) => ({ ...prev, metodoComunicacion: "HTTP" }));
    }
  }, [mostrar, mqttPermitido, formData.metodoComunicacion]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGuardar = async () => {
    try {
      const payload = {
        ...(datosaeditar || {}),
        nombre: formData.nombre,
        rutasModel: formData.ruta ? { id: Number(formData.ruta) } : null,
        velocidad: formData.velocidad !== "" ? Number(formData.velocidad) : null,
        volumen: formData.volumen !== "" ? Number(formData.volumen) : null,
        protocolosModel: formData.protocoloId ? { id: Number(formData.protocoloId) } : null,
        metodoComunicacion: formData.metodoComunicacion,
        estado: Boolean(formData.estado),
      };

      if (isEdit) await editar(payload);
      else await guardar(payload);

      cerrar();
    } catch (error) {
      console.error("Error al guardar/editar dispositivo:", error);
    }
  };

  const disableSave = !formData.nombre || !formData.ruta || !formData.protocoloId;

  return (
    <Modal show={mostrar} onHide={cerrar} centered dialogClassName="dispositivos-modal-shell">
      <Modal.Header closeButton>
        <Modal.Title>{title || (isEdit ? "Editar dispositivo" : "Nuevo dispositivo")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div style={{ marginBottom: 12, opacity: 0.9, fontSize: 13 }}>
          Completa nombre, ruta, protocolo y método de comunicación.
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Nombre del dispositivo</Form.Label>
          <Form.Control
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleInputChange}
            placeholder="DISP-001"
          />
        </Form.Group>

        {loadingRutas && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Spinner size="sm" /> <span>Cargando rutas...</span>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Ruta</Form.Label>
          <Form.Select name="ruta" value={formData.ruta} onChange={handleInputChange} disabled={!empresaId || loadingRutas}>
            <option value="">Seleccionar ruta</option>
            {rutas.map((ruta) => (
              <option key={ruta.id} value={String(ruta.id)}>
                {ruta.nomruta}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Protocolo</Form.Label>
          <Form.Select
            name="protocoloId"
            value={formData.protocoloId}
            onChange={handleInputChange}
            disabled={loadingProtocolos}
          >
            <option value="">Seleccionar protocolo</option>
            {protocolos.map((protocolo) => (
              <option key={protocolo.id} value={String(protocolo.id)}>
                {protocolo.codigo ?? protocolo.nombre ?? `Protocolo ${protocolo.id}`}
              </option>
            ))}
          </Form.Select>
          {loadingProtocolos && <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>Cargando protocolos...</div>}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Método de comunicación</Form.Label>
          <Form.Select
            name="metodoComunicacion"
            value={formData.metodoComunicacion}
            onChange={handleInputChange}
          >
            <option value="HTTP">HTTP</option>
            <option value="HTTPS">HTTPS</option>
            {mqttPermitido && <option value="MQTT">MQTT</option>}
          </Form.Select>
          {!mqttPermitido && (
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
              MQTT solo está disponible para MT-3 y MT-3-JSON.
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Velocidad</Form.Label>
          <Form.Control type="number" name="velocidad" value={formData.velocidad} onChange={handleInputChange} />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Volumen</Form.Label>
          <Form.Control type="number" name="volumen" value={formData.volumen} onChange={handleInputChange} />
        </Form.Group>

      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={cerrar}>
          Cerrar
        </Button>
        <Button variant="primary" onClick={handleGuardar} disabled={disableSave}>
          Guardar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
